import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw, Sparkles, X } from 'lucide-react';

import { DETECTIVE_TOUR_LENGTH, DETECTIVE_TOUR_STEPS } from './detectiveTour';
import { useDetectiveTourStore } from '@/store/detectiveTourStore';
import { useAskAiStore } from '@/store/askAiStore';
import { cn } from '@/lib/utils';

/**
 * The narration card — fleetGUARDIAN navy, bottom-left, above the map's own
 * controls (`z-[1400]`; the app's highest normal layer is the topbar at
 * `z-40`, and MapLibre's controls sit in the low hundreds).
 *
 * Bottom-LEFT is deliberate: the Ask AI trigger owns bottom-right, and the
 * fraud map's ranked hotspot list occupies the right column.
 */
export default function DetectiveTourOverlay(): JSX.Element | null {
  const active = useDetectiveTourStore((s) => s.active);
  const stepIndex = useDetectiveTourStore((s) => s.stepIndex);
  const next = useDetectiveTourStore((s) => s.next);
  const back = useDetectiveTourStore((s) => s.back);
  const exit = useDetectiveTourStore((s) => s.exit);
  const goTo = useDetectiveTourStore((s) => s.goTo);
  const start = useDetectiveTourStore((s) => s.start);
  const openAskAi = useAskAiStore((s) => s.open);
  const askAi = useAskAiStore((s) => s.ask);

  const step = DETECTIVE_TOUR_STEPS[stepIndex];

  // Hold the card back for `waitMs` so the page it just navigated to gets a
  // beat to paint before the narration lands on top of it.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!active) return;
    setSettled(false);
    const delay = DETECTIVE_TOUR_STEPS[stepIndex]?.waitMs ?? 0;
    if (delay <= 0) {
      setSettled(true);
      return;
    }
    const t = window.setTimeout(() => setSettled(true), delay);
    return () => window.clearTimeout(t);
  }, [active, stepIndex]);

  if (!active || !step) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === DETECTIVE_TOUR_LENGTH - 1;
  const chips = step.askChips ?? [];

  return (
    <section
      // Non-modal narration, not a dialog: the presenter keeps interacting
      // with the page underneath it.
      role="region"
      aria-live="polite"
      aria-label={`Guided tour, step ${stepIndex + 1} of ${DETECTIVE_TOUR_LENGTH}: ${step.title}`}
      className={cn(
        'fixed bottom-4 left-4 z-[1400] w-[calc(100vw-2rem)] max-w-[27rem]',
        'overflow-hidden rounded-xl border border-white/15 bg-brand text-brand-fg',
        'transition-opacity duration-300 motion-reduce:transition-none',
        // Invisible during the settle delay must also mean unclickable —
        // otherwise Next is an invisible hit target over the page.
        settled ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      // Soft-green signature glow, only while the tour is running.
      style={{
        boxShadow:
          '0 0 0 1px rgba(134, 239, 172, 0.45), 0 0 20px -2px rgba(134, 239, 172, 0.30), 0 24px 48px -16px rgba(0, 0, 0, 0.55)',
      }}
    >
      {/* Numbered dot rail — filled = done, ringed + pulsing = current. Each
          dot is a jump target so the presenter can skip a beat live. */}
      <div className="flex items-center gap-[3px] border-b border-white/10 px-4 pb-2 pt-2.5 sm:px-5">
        {DETECTIVE_TOUR_STEPS.map((s, i) => {
          const done = i < stepIndex;
          const current = i === stepIndex;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => goTo(i)}
              title={`${i + 1}. ${s.title}`}
              aria-label={`Go to step ${i + 1}: ${s.title}`}
              aria-current={current ? 'step' : undefined}
              className="group flex h-4 flex-1 items-center justify-center focus-visible:outline-none"
            >
              <span
                className={cn(
                  'block rounded-full transition-all duration-300 motion-reduce:transition-none',
                  current
                    ? 'h-2.5 w-2.5 bg-brand-fg ring-2 ring-emerald-300/70 ring-offset-1 ring-offset-brand motion-safe:animate-pulse'
                    : done
                      ? 'h-1.5 w-1.5 bg-white/85 group-hover:bg-brand-fg'
                      : 'h-1.5 w-1.5 bg-white/25 group-hover:bg-white/50',
                )}
              />
            </button>
          );
        })}
      </div>

      {/* Re-keyed per step so the 250ms fade/slide replays on every beat. */}
      <div
        key={step.id}
        className="tour-step-in max-h-[min(72vh,34rem)] overflow-y-auto px-4 pb-3 pt-3 sm:px-5"
      >
        <div className="flex items-start gap-3">
          <span aria-hidden className="mt-0.5 shrink-0 text-3xl leading-none">
            {step.emoji}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-white/60">
              Step {stepIndex + 1} of {DETECTIVE_TOUR_LENGTH}
            </p>
            <h2 className="mt-0.5 text-[16px] font-extrabold leading-tight tracking-tight">
              {step.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={exit}
            aria-label="Exit tour"
            className="-mr-1 shrink-0 rounded-md p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-brand-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-2.5 text-[13px] leading-[1.65] text-white/90">
          {step.narration}
        </p>

        {step.bullets && step.bullets.length > 0 && (
          <ul className="mt-2.5 space-y-1.5 border-l border-emerald-300/25 pl-3">
            {step.bullets.map((b) => (
              <li
                key={b}
                className="flex gap-2 text-[12px] leading-[1.55] text-white/85"
              >
                <span aria-hidden className="mt-[3px] shrink-0 text-emerald-300">
                  ▸
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        {chips.length > 0 && (
          <div className="mt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-white/50">
              Try one
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {chips.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => askAi(q)}
                  className={cn(
                    'rounded-full border border-emerald-300/40 bg-white/5 px-2.5 py-1',
                    'text-[12px] font-medium text-white/90 transition-colors',
                    'hover:border-emerald-300/70 hover:bg-white/15 hover:text-brand-fg',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
                  )}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={back}
            disabled={isFirst}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[13px] font-medium',
              'text-white/80 transition-colors hover:bg-white/10 hover:text-brand-fg',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
              'disabled:pointer-events-none disabled:opacity-40',
            )}
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>

          {chips.length > 0 && (
            <button
              type="button"
              onClick={openAskAi}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-brand-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            >
              <Sparkles className="h-4 w-4" />
              Ask AI
            </button>
          )}

          {isLast && (
            <button
              type="button"
              onClick={start}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-brand-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            >
              <RotateCcw className="h-4 w-4" />
              Restart
            </button>
          )}

          <button
            type="button"
            onClick={next}
            className={cn(
              'ml-auto inline-flex items-center gap-1 rounded-md bg-brand-fg px-3 py-1.5',
              'text-[13px] font-semibold text-brand transition-opacity hover:opacity-90',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
            )}
          >
            {isLast ? 'Finish' : 'Next'}
            {!isLast && <ChevronRight className="h-4 w-4" />}
          </button>
        </div>

        <p className="mt-2 text-[11px] text-white/45">
          Use ← → to move, Home to restart, Esc to exit.
        </p>
      </div>
    </section>
  );
}
